// Единый формат чисел и денег (DESIGN.md §3): `$1 234.50`, `1 250 000 сум`, проценты с одним знаком.
// Деньги и целые — общие с сервером (@autoaff/schemas/money): один формат в интерфейсе, Telegram и ответах агенту.
export { groupDigits, int, money, signedUsd, sum, usd } from '@autoaff/schemas/money';

export function pct(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

const TZ = 'Asia/Tashkent';

export function time(iso: string | Date): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
}

const NB = ' ';
const dayKey = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: TZ });

/** «сегодня», «вчера», «завтра», «24 сент.», в другом году — «24 сент. 2025». Без переносов внутри. */
export function dateShort(iso: string | Date, now = new Date()): string {
  const d = new Date(iso);
  const k = dayKey(d);
  const shift = (days: number) => dayKey(new Date(now.getTime() + days * 86_400_000));
  if (k === dayKey(now)) return 'сегодня';
  if (k === shift(-1)) return 'вчера';
  if (k === shift(1)) return 'завтра';
  const year = k.slice(0, 4) === dayKey(now).slice(0, 4) ? {} : ({ year: 'numeric' } as const);
  return d
    .toLocaleDateString('ru-RU', { timeZone: TZ, day: 'numeric', month: 'short', ...year })
    .replace(/\s*г\.$/, '')
    .replace(/\s/g, NB);
}

/**
 * Дата и время в списках — одно правило везде: «сегодня, 09:37», «вчера, 18:02», «завтра, 03:30», «24 сент., 09:37».
 * Не переносится по частям («сегодня,» / «09:37»).
 */
export function when(iso: string | Date): string {
  return `${dateShort(iso)},${NB}${time(iso)}`;
}

/** Срок «до …»: сегодня — только время («23:00»), иначе с днём («завтра, 03:30», «29 сент., 23:00»). */
export function till(iso: string | Date): string {
  return dateShort(iso) === 'сегодня' ? time(iso) : when(iso);
}

/** «через 3 ч 52 мин», «через 40 с». */
export function inTime(iso: string | Date, from = Date.now()): string {
  const s = Math.max(0, Math.round((new Date(iso).getTime() - from) / 1000));
  if (s < 60) return `через ${s} с`;
  const m = Math.floor(s / 60);
  if (m < 60) return `через ${m} мин`;
  const h = Math.floor(m / 60);
  return `через ${h} ч${m % 60 ? ` ${m % 60} мин` : ''}`;
}

export function duration(ms: number): string {
  if (ms < 1000) return `${ms} мс`;
  const s = ms / 1000;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} с`;
  return `${Math.floor(s / 60)} мин ${Math.round(s % 60)} с`;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}
